import type { ReactNode } from "react";
import { CreatorAvatar, useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { statusLabel } from "./api.ts";
import { StatusPill as Pill, type PillTone } from "./look/parts.tsx";

export { OutLink, ULink } from "./look/parts.tsx";

/**
 * A creator's name with their photo beside it, from the one store every
 * module reads (Jay, 2026-10-01: a photo wherever a creator is named). The
 * queue's pages ask through the queue's permission, the flows' pages through
 * theirs, so the photo shows wherever the name does.
 */
export function useUpCreatorPhotos(): CreatorPhotos {
  const { can } = useMe();
  return useCreatorPhotos(can("uploader.queue:view") ? "/api/m/uploader" : "/api/m/uploader/flows");
}

export function UpCreator({ name, photos, size = 18, children }: { name: string; photos: CreatorPhotos; size?: number; children?: ReactNode }) {
  return (
    // Inline, not a class: it is also drawn on Now, outside the Uploader's root, where look.css does not reach.
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0, verticalAlign: "middle" }}>
      <CreatorAvatar name={name} src={photos(name)} size={size} />
      {/* Creator names read in capitals in every module, whatever case they were saved in. */}
      {children ?? name.toUpperCase()}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   What every Uploader page shares: a status as a pill, a short folder id and
   the words for an error. The pages themselves are drawn in the upload
   system's look (look/).
   ────────────────────────────────────────────────────────────────────────── */

const TONE: Record<string, PillTone> = {
  FAILED: "bad",
  RUNNING: "run",
  MOVING: "run",
  READY_TO_MOVE: "run",
  PAUSE_REQUESTED: "warn",
  PAUSED: "warn",
  WAITING: "idle",
};

export function StatusPill({ status, attempts = 0 }: { status: string; attempts?: number }) {
  return (
    <Pill tone={TONE[status] ?? "idle"}>
      {statusLabel(status)}
      {status === "FAILED" && attempts ? `, ${attempts} of 3 retries` : ""}
    </Pill>
  );
}

/** The last path segment of a Drive link, shortened, for a cell with no room for a URL. */
export function folderId(link: string): string {
  const m = link.match(/[-\w]{10,}/g);
  const id = m?.[m.length - 1] ?? link;
  return id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-5)}` : id;
}

export function messageOf(error: unknown, fallback = "Something went wrong. Try again in a moment."): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
